import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { AlertCircle, CheckCircle2, Loader2, Bug } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
}

export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose, userEmail }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState(userEmail || '');
  const [priority, setPriority] = useState('MEDIUM');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const browserInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        devicePixelRatio: window.devicePixelRatio,
      };

      const response = await apiFetch('/api/bug-reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          description,
          email,
          priority,
          browserInfo,
        }),
      });

      if (!response.ok) throw new Error('Failed to submit report');

      setIsSuccess(true);
      toast.success('Bug report submitted successfully!');
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setTitle('');
        setDescription('');
      }, 2000);
    } catch (error) {
      toast.error('Error submitting bug report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white border-zinc-100 rounded-3xl shadow-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-[0.2em] text-zinc-950">
            <Bug className="w-4 h-4 text-cyan-600" />
            Report a Bug
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-[11px] font-medium uppercase tracking-wider">
            Help us improve the simulation suite.
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
            <div className="w-16 h-16 bg-cyan-50 rounded-full flex items-center justify-center border border-cyan-500/10">
              <CheckCircle2 className="w-8 h-8 text-cyan-600" />
            </div>
            <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-900">Submission Successful</h3>
            <p className="text-[10px] uppercase tracking-wide text-zinc-400">Our team has been notified.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-[10px] font-medium uppercase tracking-[0.1em] text-zinc-400 px-1">Issue Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief summary..."
                className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-[12px] text-zinc-950 focus:border-cyan-600/30 focus:bg-white transition-all outline-none"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-medium uppercase tracking-[0.1em] text-zinc-400 px-1">Detailed Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="How can we reproduce it?"
                className="w-full px-4 py-3 bg-zinc-50 border border-zinc-100 rounded-xl text-[12px] text-zinc-950 focus:border-cyan-600/30 focus:bg-white transition-all outline-none min-h-[100px] resize-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-medium uppercase tracking-[0.1em] text-zinc-400 px-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-[12px] text-zinc-950 focus:border-cyan-600/30 transition-all outline-none appearance-none"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-medium uppercase tracking-[0.1em] text-zinc-400 px-1">Email (Optional)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="updates@example.com"
                  className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-100 rounded-xl text-[12px] text-zinc-950 focus:border-cyan-600/30 transition-all outline-none"
                />
              </div>
            </div>

            <DialogFooter className="pt-6 border-t border-zinc-50">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="rounded-xl px-6 text-[10px] font-medium uppercase tracking-widest border-zinc-100 text-zinc-400 hover:bg-zinc-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-white border border-cyan-600/30 text-cyan-600 shadow-sm hover:shadow-md hover:bg-cyan-50/50 rounded-xl px-8 text-[10px] font-medium uppercase tracking-widest disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
